import { useCallback, useEffect, useMemo } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { HydratedCallRecord } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { Call } from '@weave/schema-gen-ts/dist/shared/call-intelligence/v2/call_intelligence.pb';
import { CallIntelTypes } from '@frontend/api-call-intel';
import {
  CallIntelAccordion,
  getHighlightedCitation,
  TaskAccordion,
  UnsavedChangesModal,
} from '@frontend/call-intel-core';
import { Divider } from '@frontend/divider';
import { useTranslation } from '@frontend/i18n';
import { useGlobalPanel } from '@frontend/panel-engine';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { theme } from '@frontend/theme';
import { Accordion, Tray, useModalControl } from '@frontend/design-system';
import { useCallInsightController } from '../hooks';
import { useCallInsightPanelData } from '../hooks/use-call-insight-panel-data';
import { useCallInsightPanelState } from '../hooks/use-call-insight-panel-state';
import { useCallInsightPanelShallowStore } from '../hooks/use-call-insight-panel-store';
import { useTaskHandlers } from '../hooks/use-task-handlers';
import { CallInsightAccordionType, CallInsightPanelSource } from '../types';
import { CallInsightContent } from './call-insight-content';
import { CallInsightHeader } from './call-insight-header';
import { CallInsightPanelLoader } from './call-insight-panel-loader';
import { NavigationButtons } from './navigation-buttons';
import { CallDetailsSection } from './sections/call-details-section';
import { CallInsightContactSection } from './sections/call-insight-contact-section';
import { CallInsightTagsSection } from './sections/call-insight-tags-section';
import { CallRecordingSection } from './sections/call-recording-section';

type CallInsightPanelContentProps = {
  onClose?: () => void;
  panelStyles?: Interpolation<Theme>;
  source: CallInsightPanelSource;
};

const CallInsightPanelContent = ({ panelStyles, source, onClose }: CallInsightPanelContentProps) => {
  const { t } = useTranslation('call-insight-panel');

  const { modalProps, triggerProps } = useModalControl();

  const { isLoading, emitEvent, goToNextCall, goToPreviousCall } = useCallInsightController({
    source,
  });
  const { contexts, closeContext, updateCallInsight } = useCallInsightPanelShallowStore(
    'contexts',
    'closeContext',
    'updateCallInsight'
  );

  // Extract context and call info
  const panelContext = contexts[source];
  const callRecord = panelContext?.selectedCall as HydratedCallRecord;
  const hasNextCall = !!panelContext?.nextCallItem;
  const hasPreviousCall = !!panelContext?.previousCallItem;
  const { defaultAccordion, isDemoAccount, highlightedTaskId: defaultHighlightedTaskId } = panelContext;

  const {
    activeAccordion,
    highlightedTaskId,
    unsavedChangesConfig,
    setActiveAccordion,
    setHighlightedTaskId,
    setUnsavedChangesConfig,
  } = useCallInsightPanelState({ defaultAccordion, defaultHighlightedTaskId });

  const {
    callMetadata,
    callRecordingPath,
    contactInfo,
    isLoadingCallMetadata,
    isTaskFetching,
    taskQueryKey,
    refetchCallMetadata,
  } = useCallInsightPanelData({ callRecord, isDemoAccount });

  const { handleTasksUpdate, handleTaskDelete } = useTaskHandlers({
    queryKey: taskQueryKey,
    emitEvent,
    updateCallInsight,
  });

  const highlightedCitation = useMemo(
    () => getHighlightedCitation(callMetadata.tasks, highlightedTaskId),
    [callMetadata.tasks, highlightedTaskId]
  );

  const confirmBeforeAction = useCallback(
    (action: () => void) => {
      const { dontAskAgain, hasUnsavedChanges } = unsavedChangesConfig;
      if (!dontAskAgain && hasUnsavedChanges) {
        setUnsavedChangesConfig({ actionType: 'SHOW_MODAL', hasUnsavedChanges: true });
      } else {
        action();
      }
    },
    [setUnsavedChangesConfig, unsavedChangesConfig]
  );

  const handleAccordionChange = useCallback(
    (value: string | null) => {
      setActiveAccordion(value as CallInsightAccordionType | null);
      setHighlightedTaskId(null);
    },
    [setActiveAccordion, setHighlightedTaskId]
  );

  const handleUpdateCall = useCallback(
    (call: CallIntelTypes.Call) => {
      updateCallInsight({ updatedCallIntelInsights: call as Call });
      emitEvent('callAnalysisUpdated', { call: call as Call });
    },
    [emitEvent, updateCallInsight]
  );

  const handleHighlightTask = useCallback(
    (taskId: string) => {
      setActiveAccordion('callIntel');
      setHighlightedTaskId(taskId);
    },
    [setActiveAccordion, setHighlightedTaskId]
  );

  const handleClose = useCallback(() => {
    const { hasUnsavedChanges, dontAskAgain } = unsavedChangesConfig;
    if (!dontAskAgain && hasUnsavedChanges) {
      setUnsavedChangesConfig({ hasUnsavedChanges: true, actionType: 'SHOW_MODAL' });
      return;
    }
    closeContext();
    !!onClose && onClose();
  }, [closeContext, onClose, setUnsavedChangesConfig, unsavedChangesConfig]);

  useEffect(() => {
    const { actionType, dontAskAgain, hasUnsavedChanges } = unsavedChangesConfig;
    if (!dontAskAgain && actionType === 'SHOW_MODAL' && hasUnsavedChanges) {
      triggerProps.onClick();
    }
  }, [triggerProps, unsavedChangesConfig]);

  return (
    <div css={[styles.wrapper, panelStyles]}>
      <CallInsightHeader onClose={handleClose} />
      <CallInsightContent isLoading={isLoading}>
        <CallInsightContent.Lead>
          <CallInsightContactSection
            callDirection={callRecord?.direction ?? ''}
            contact={{
              ...contactInfo,
              defaultName: t('Unknown'),
            }}
            locationId={contactInfo.locationId}
          />
          <CallRecordingSection call={callRecord} callRecordingPath={callRecordingPath} />
          <CallInsightTagsSection call={callRecord} />
          <Divider css={styles.divider} />
        </CallInsightContent.Lead>
        <CallInsightContent.Body>
          <Accordion controlledValue={activeAccordion} css={styles.accordion} onChange={handleAccordionChange}>
            <Accordion.Item value='callIntel'>
              <CallIntelAccordion.Header />
              <CallIntelAccordion.Body
                call={callRecord.callIntelInsights as CallIntelTypes.Call}
                source={source}
                isLoading={isLoadingCallMetadata || isTaskFetching}
                isDemoAccount={isDemoAccount}
                refetchCallMetadata={refetchCallMetadata}
                callMetadata={callMetadata}
                highlightedCitation={highlightedCitation}
                onUpdateCall={handleUpdateCall}
                unsavedEditChangesConfig={unsavedChangesConfig}
                setUnsavedEditChangesConfig={setUnsavedChangesConfig}
              />
            </Accordion.Item>

            <Accordion.Item value='tasks'>
              <TaskAccordion.Header tasks={callMetadata.tasks} />
              <TaskAccordion.Body
                call={callRecord.callIntelInsights as CallIntelTypes.Call}
                callParticipantsMetadata={callMetadata.callParticipantsMetadata}
                isLoading={isLoadingCallMetadata || isTaskFetching}
                tasks={callMetadata.tasks}
                isDemoAccount={isDemoAccount}
                onHighlightTask={handleHighlightTask}
                onUpdateTasks={handleTasksUpdate}
                onDeleteTask={handleTaskDelete}
              />
            </Accordion.Item>

            <Accordion.Item value='aboutThisCall'>
              <CallDetailsSection call={callRecord} />
            </Accordion.Item>
          </Accordion>
        </CallInsightContent.Body>
        <CallInsightContent.Footer>
          <NavigationButtons
            disableNext={!hasNextCall}
            disablePrevious={!hasPreviousCall}
            onNextClick={() => confirmBeforeAction(goToNextCall)}
            onPreviousClick={() => confirmBeforeAction(goToPreviousCall)}
            trackingIdBase='call-insight-panel'
          />
        </CallInsightContent.Footer>
      </CallInsightContent>
      {unsavedChangesConfig.hasUnsavedChanges && unsavedChangesConfig.actionType === 'SHOW_MODAL' && (
        <UnsavedChangesModal
          source={source}
          modalProps={modalProps}
          unsavedEditChangesConfig={unsavedChangesConfig}
          setUnsavedEditChangesConfig={setUnsavedChangesConfig}
        />
      )}
    </div>
  );
};

export const CallInsightDemoTray = () => {
  const { setShow } = useSlidePanelShallowStore('setShow');
  const { contexts, activeSource } = useCallInsightPanelShallowStore('contexts', 'activeSource');
  if (!activeSource) return null;
  const ctx = contexts[activeSource];
  if (!ctx?.selectedCall) return null;

  const handleClose = () => {
    setShow(false, 'callInsight');
  };

  return (
    <Tray mountTarget='[data-settings-modal-content]' width='medium' css={{ padding: 0 }} show onClose={handleClose}>
      <CallInsightPanelContent source={activeSource} key={ctx.selectedCall.id} onClose={handleClose} />
    </Tray>
  );
};

export const CallInsightPanel = () => {
  const { isOpen: isGlobalOpen, close: globalClose } = useGlobalPanel('call-insight');
  const { contexts, activeSource } = useCallInsightPanelShallowStore('contexts', 'activeSource');
  if (!activeSource) return <CallInsightPanelLoader withHeader onClose={globalClose} />;
  const ctx = contexts[activeSource];
  if (!ctx?.selectedCall) return <CallInsightPanelLoader withHeader onClose={globalClose} />;
  // The key helps to rerender the component when the call changes to avoid stale content and state
  return isGlobalOpen ? (
    <CallInsightPanelContent
      source={activeSource}
      key={ctx.selectedCall.id}
      onClose={globalClose}
      panelStyles={{ overflowY: 'auto' }}
    />
  ) : null;
};

const styles = {
  wrapper: css`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden auto;
  `,
  accordion: css`
    flex: 1 1 100%;

    > div {
      box-shadow: none;
      border: 1px solid ${theme.colors.neutral20};
      margin-bottom: ${theme.spacing(2)};
    }

    > div h3 button section div svg {
      fill: ${theme.colors.neutral70};
    }

    div[data-active='true'] h3 button {
      background-color: ${theme.colors.neutral5};
      border-bottom: 1px solid ${theme.colors.neutral10};

      section div svg {
        fill: ${theme.colors.neutral90};
      }
    }
  `,
  feedbackFooter: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: row;
  `,
  divider: css`
    margin: ${theme.spacing(0)};
    border-color: ${theme.colors.neutral20};
  `,
};
